import React, { useContext, useState, useEffect } from "react";
import {
  Modal,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import ModalHeader from "../../modals/ModalHeader";
import ModalFooter from "../../modals/ModalFooter";
import { postJsonData } from "../../network/ApiController";
import ApiEndpoints from "../../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../../utils/ToastUtil";
import AuthContext from "../../store/AuthContext";
import SmallLoader from "../../component/loading-screen/SmallLoader";

const AddLeaveModal = ({ setOpenModal, openModal, refresh }) => {
  const authCtx = useContext(AuthContext);
  const user = authCtx.user;

  const [newLeaveReq, setNewLeaveReq] = useState({
    user_id: user.id,
    emp_name: user.name,
    leave_type: "",
    work_type: "",
    request_nature: "leave",
    from: "",
    to: "",
    reason: "",
  });

  const leaveTypes = [
    { value: "sl", label: "Sick Leave" },
    { value: "cl", label: "Casual Leave" },
    { value: "el", label: "Earned Leave" },
    { value: "ml", label: "Maternity Leave" },
    { value: "pl", label: "Paternity Leave" },
    { value: "bl", label: "Bereavement Leave" },
    { value: "compensatory", label: "Compensatory Leave" },
    { value: "unpaid", label: "Unpaid Leave" },
  ];

  const sessions = [
    { value: "full_day", label: "Full Day" },
    { value: "morning_half", label: "Morning Half Day" },
    { value: "evening_half", label: "Evening Half Day" },
  ];

  const [image, setImage] = useState(null);
  const [request, setRequest] = useState(false);
  const [error, setError] = useState("");

  // Auto-set work_type based on leave type
  useEffect(() => {
    const leaveType = newLeaveReq.leave_type;
    
    // If leave type is selected
    if (leaveType) {
      // For SL and CL, we don't auto-set - let user choose from dropdown
      if (["sl", "cl"].includes(leaveType)) {
        // Only clear if previously set by auto-fill, but preserve user selection
        if (newLeaveReq.work_type && !["full_day", "morning_half", "evening_half"].includes(newLeaveReq.work_type)) {
          setNewLeaveReq(prev => ({
            ...prev,
            work_type: ""
          }));
        }
      } else {
        // For all other leave types, set to full_day
        setNewLeaveReq(prev => ({
          ...prev,
          work_type: "full_day"
        }));
      }
    }
  }, [newLeaveReq.leave_type]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setNewLeaveReq((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    setImage(file);
  };

  const validateForm = () => {
    const { leave_type, work_type, from, to, reason } = newLeaveReq;

    // Check basic required fields
    if (!leave_type || !from || !to || !reason) {
      setError("Please fill all required fields.");
      return false;
    }

    // Check if dates are valid
    const fromDate = new Date(from);
    const toDate = new Date(to);

    if (fromDate > toDate) {
      setError("'From' date cannot be after 'To' date.");
      return false;
    }

    // For sick leave and casual leave, work_type is required (user must select)
    if (["sl", "cl"].includes(leave_type) && !work_type) {
      setError("Session is required for Sick Leave and Casual Leave.");
      return false;
    }

    // For other leave types, work_type should be set to full_day (auto-filled)
    if (!["sl", "cl"].includes(leave_type) && work_type !== "full_day") {
      // This shouldn't happen due to useEffect, but just in case
      setNewLeaveReq(prev => ({
        ...prev,
        work_type: "full_day"
      }));
    }

    // Image/receipt is optional - no validation needed
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    // Ensure work_type is properly set before submission
    if (!["sl", "cl"].includes(newLeaveReq.leave_type)) {
      newLeaveReq.work_type = "full_day";
    }

    if (!validateForm()) {
      return;
    }

    const formData = new FormData();

    // Append all leave request fields (only if they have values)
    Object.entries(newLeaveReq).forEach(([key, value]) => {
      // Only append if value exists and is not empty
      if (value && value.toString().trim() !== "") {
        formData.append(key, value);
      }
    });

    // Append image only if it exists - this makes it optional
    // If no image selected, nothing is appended to formData
    if (image) {
      formData.append("image", image);
    }

    // Optional: Log formData contents for debugging (you can remove this in production)
    // for (let pair of formData.entries()) {
    //   console.log(pair[0] + ': ' + pair[1]);
    // }

    postJsonData(
      ApiEndpoints.Add_Leave,
      formData,
      setRequest,
      (res) => {
        okSuccessToast(res.data.message);
        handleModalClose();
        if (refresh) refresh();
      },
      (err) => apiErrorToast(err),
      true // This indicates it's multipart/form-data
    );
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setNewLeaveReq({
      user_id: user.id,
      emp_name: user.name,
      leave_type: "",
      work_type: "",
      from: "",
      to: "",
      reason: "",
    });
    setImage(null);
    setError("");
  };

  const isFormComplete = () => {
    const { leave_type, work_type, from, to, reason } = newLeaveReq;

    // Basic required fields (image is not included in validation)
    if (!leave_type || !from || !to || !reason) {
      return false;
    }

    // For SL and CL, work_type must be selected
    if (["sl", "cl"].includes(leave_type) && !work_type) {
      return false;
    }

    // Image is optional, so form can be complete without it
    return true;
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: {
      xs: "90%",
      sm: "80%",
      md: "70%",
      lg: "600px",
      xl: "600px",
    },
    maxWidth: "600px",
    maxHeight: "90vh",
    overflowY: "auto",
    bgcolor: "background.paper",
    border: "2px solid #000",
    boxShadow: 24,
    p: {
      xs: 2,
      sm: 3,
      md: 4,
    },
    borderRadius: 2,
  };

  return (
    <Modal open={openModal} onClose={handleModalClose}>
      <Box sx={style}>
        <SmallLoader loading={request} />
        <ModalHeader title="Add Leave Request" handleClose={handleModalClose} />
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Employee ID"
                name="user_id"
                fullWidth
                disabled
                required
                value={newLeaveReq.user_id}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Employee Name"
                name="emp_name"
                fullWidth
                required
                disabled
                value={newLeaveReq.emp_name}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required size="small">
                <InputLabel>Leave Type</InputLabel>
                <Select
                  name="leave_type"
                  value={newLeaveReq.leave_type}
                  onChange={handleFieldChange}
                  label="Leave Type"
                >
                  {leaveTypes?.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Show session dropdown only for SL and CL */}
            {["sl", "cl"].includes(newLeaveReq?.leave_type) && (
              <Grid item xs={12}>
                <FormControl fullWidth required size="small">
                  <InputLabel>Session</InputLabel>
                  <Select
                    name="work_type"
                    value={newLeaveReq.work_type}
                    onChange={handleFieldChange}
                    label="Session"
                  >
                    {sessions.map((session) => (
                      <MenuItem key={session?.value} value={session?.value}>
                        {session.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                label="From"
                type="date"
                name="from"
                fullWidth
                required
                onChange={handleFieldChange}
                value={newLeaveReq.from}
                InputLabelProps={{ shrink: true }}
                size="small"
                InputProps={{
                  sx: {
                    "& input": {
                      fontSize: { xs: "14px", sm: "16px" },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="To"
                type="date"
                name="to"
                fullWidth
                required
                onChange={handleFieldChange}
                value={newLeaveReq.to}
                InputLabelProps={{ shrink: true }}
                size="small"
                InputProps={{
                  sx: {
                    "& input": {
                      fontSize: { xs: "14px", sm: "16px" },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reason"
                name="reason"
                fullWidth
                required
                multiline
                rows={3}
                onChange={handleFieldChange}
                value={newLeaveReq.reason}
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                    Receipt (Optional)
                  </Typography>
                  <input
                    accept="image/*"
                    type="file"
                    onChange={handleImageChange}
                    id="task_img"
                    style={{
                      width: "100%",
                      padding: "8px 0",
                      fontSize: "14px",
                    }}
                  />
                  {image && (
                    <Box sx={{ marginTop: 1 }}>
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                          wordBreak: "break-word",
                        }}
                      >
                        <strong>Selected Image:</strong> {image.name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </FormControl>
            </Grid>
          </Grid>

          {error && (
            <Typography
              color="error"
              sx={{
                mt: 2,
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              {error}
            </Typography>
          )}

          <ModalFooter
            onClick={handleSubmit}
            btn="Add Leave"
            disable={!isFormComplete()}
          />
        </form>
      </Box>
    </Modal>
  );
};

export default AddLeaveModal;